import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { buildApiUrl, buildPublicFileUrl } from "../config/api";
import "./AdminProducts.css";

const emptyForm = {
  productId: "",
  productType: "photo_download",
  status: "published",
  title: "",
  slug: "",
  price: "",
  category: "",
  collection: "",
  shortDescription: "",
  description: "",
  story: "",
  image: "",
  badge: "",
  featured: false,
  homepageLatest: false,
  homepageLatestOrder: "",
  licenseOptionsText: "Personal Digital Download:9.99\nCommercial License:49.99",
  includesText: "High-resolution JPG\nInstant download\nPersonal use license",
  tagsText: "",
  seoTitle: "",
  metaDescription: "",
  altText: "",
};

function slugify(text) {
  return String(text || "")
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function linesToArray(text) {
  return String(text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function licenseTextToArray(text) {
  return linesToArray(text).map((line) => {
    const [label, price] = line.split(":");
    return {
      label: (label || "Personal Digital Download").trim(),
      price: Number(price || 0),
    };
  });
}

function getField(item, keys, fallback = "") {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null && item?.[key] !== "") {
      return item[key];
    }
  }

  return fallback;
}

function getProductImage(product) {
  return getField(
    product,
    ["image", "coverImage", "cover_image", "thumbnail", "thumbnailImage"],
    ""
  );
}

function getImageSrc(imagePath) {
  if (!imagePath) return "";

  if (
    imagePath.startsWith("http://") ||
    imagePath.startsWith("https://") ||
    imagePath.startsWith("blob:") ||
    imagePath.startsWith("data:")
  ) {
    return imagePath;
  }

  if (imagePath.startsWith("/")) {
    return buildPublicFileUrl(imagePath);
  }

  return imagePath;
}

function productToForm(product) {
  return {
    productId: product.productId || product.product_id || "",
    productType: product.productType || product.product_type || "photo_download",
    status: product.status || "published",
    title: product.title || "",
    slug: product.slug || "",
    price: product.price || "",
    category: product.category || "",
    collection: product.collection || product.collectionName || "",
    shortDescription: product.shortDescription || "",
    description: product.description || "",
    story: product.story || "",
    image: product.image || "",
    badge: product.badge || "",
    featured: Boolean(product.featured || product.isFeatured),
    homepageLatest: Boolean(product.homepageLatest || product.homepage_latest),
    homepageLatestOrder: product.homepageLatestOrder ?? product.homepage_latest_order ?? "",
    licenseOptionsText: Array.isArray(product.licenseOptions)
      ? product.licenseOptions.map((item) => `${item.label}:${item.price}`).join("\n")
      : "",
    includesText: Array.isArray(product.includes) ? product.includes.join("\n") : "",
    tagsText: Array.isArray(product.tags) ? product.tags.join("\n") : "",
    seoTitle: product.seoTitle || "",
    metaDescription: product.metaDescription || "",
    altText: product.altText || "",
  };
}

async function readJsonResponse(response, label) {
  const text = await response.text();

  try {
    return text ? JSON.parse(text) : {};
  } catch {
    throw new Error(
      `${label} returned HTML instead of JSON. Check the backend route and API configuration.`
    );
  }
}

function AdminProducts() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formRef = useRef(null);
  const listRef = useRef(null);
  const autoOpenedRef = useRef("");

  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [updatingProductId, setUpdatingProductId] = useState(null);
  const [optimizationStatuses, setOptimizationStatuses] = useState({});

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [featuredFilter, setFeaturedFilter] = useState("all");
  const [sortBy, setSortBy] = useState("product-id-asc");

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const [response, optimizationResponse] = await Promise.all([
        fetch(buildApiUrl("/products/admin/all"), { credentials: "include", headers: { Accept: "application/json" } }),
        fetch(buildApiUrl("/admin/image-optimizer/product-statuses"), { credentials: "include", headers: { Accept: "application/json" } }),
      ]);

      const data = await readJsonResponse(response, "Products API");
      const optimizationData = await readJsonResponse(optimizationResponse, "Image optimization API");

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Failed to fetch products.");
      }

      setProducts(data.products || []);
      if (optimizationResponse.ok && optimizationData.success) setOptimizationStatuses(optimizationData.statuses || {});
    } catch (err) {
      setError(err.message || "Failed to fetch products.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const stats = useMemo(() => {
    const active = products.filter((item) => item.status !== "deleted");

    return {
      total: active.length,
      published: active.filter((item) => item.status === "published").length,
      unpublished: active.filter((item) => item.status !== "published").length,
      featured: active.filter((item) => item.featured || item.isFeatured).length,
    };
  }, [products]);

  const filteredProducts = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    let result = products.filter((product) => product.status !== "deleted");

    if (query) {
      result = result.filter((product) => {
        const searchable = [
          product.productId,
          product.productCode,
          product.title,
          product.slug,
          product.category,
          product.collection,
          product.status,
        ]
          .join(" ")
          .toLowerCase();

        return searchable.includes(query);
      });
    }

    if (statusFilter !== "all") {
      result = result.filter((product) => product.status === statusFilter);
    }

    if (featuredFilter === "featured") {
      result = result.filter((product) => product.featured || product.isFeatured);
    }

    if (featuredFilter === "not-featured") {
      result = result.filter((product) => !product.featured && !product.isFeatured);
    }

    return [...result].sort((a, b) => {
      const aCode = String(a.productId || a.productCode || "");
      const bCode = String(b.productId || b.productCode || "");

      if (sortBy === "product-id-asc") {
        return aCode.localeCompare(bCode, undefined, { numeric: true, sensitivity: "base" });
      }

      if (sortBy === "product-id-desc") {
        return bCode.localeCompare(aCode, undefined, { numeric: true, sensitivity: "base" });
      }

      if (sortBy === "oldest") {
        return Number(a.id || a._id || 0) - Number(b.id || b._id || 0);
      }

      if (sortBy === "price-high") {
        return Number(b.price || 0) - Number(a.price || 0);
      }

      if (sortBy === "price-low") {
        return Number(a.price || 0) - Number(b.price || 0);
      }

      if (sortBy === "title") {
        return String(a.title || "").localeCompare(String(b.title || ""));
      }

      return Number(b.id || b._id || 0) - Number(a.id || a._id || 0);
    });
  }, [products, searchText, statusFilter, featuredFilter, sortBy]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setForm((prev) => {
      const next = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "title" && !editingId) {
        next.slug = slugify(value);
      }

      return next;
    });
  };

  const buildPayload = () => ({
    productId: form.productId.trim(),
    brand: "MayaChitro",
    brandSlug: "mayachitro",
    productType: form.productType,
    status: form.status,
    title: form.title.trim(),
    slug: form.slug.trim() || slugify(form.title),
    price: Number(form.price),
    category: form.category.trim(),
    collection: form.collection.trim(),
    shortDescription: form.shortDescription.trim(),
    description: form.description.trim(),
    story: form.story.trim(),
    image: form.image.trim(),
    badge: form.badge.trim(),
    featured: Boolean(form.featured),
    homepageLatest: Boolean(form.homepageLatest),
    homepageLatestOrder: form.homepageLatest ? Number(form.homepageLatestOrder || 1) : null,
    licenseOptions: licenseTextToArray(form.licenseOptionsText),
    includes: linesToArray(form.includesText),
    tags: linesToArray(form.tagsText),
    seoTitle: form.seoTitle.trim(),
    metaDescription: form.metaDescription.trim(),
    altText: form.altText.trim(),
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    setIsSaving(true);

    try {
      const payload = buildPayload();

      if (!payload.productId || !payload.title || !payload.slug || !payload.price) {
        throw new Error("Product ID, title, slug, and price are required.");
      }

      const url = editingId
        ? buildApiUrl(`/products/${editingId}`)
        : buildApiUrl("/products");

      const response = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await readJsonResponse(response, "Save Product API");

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Could not save product.");
      }

      setSuccess(editingId ? "Product updated successfully." : "Product added successfully.");
      setForm(emptyForm);
      setEditingId(null);
      setEditingTitle("");

      await fetchProducts();

      setTimeout(() => {
        listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } catch (err) {
      setError(err.message || "Could not save product.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (product) => {
    const id = product.id || product._id;

    setEditingId(id);
    setEditingTitle(product.title || product.productId || "Selected product");
    setForm(productToForm(product));
    setError("");
    setSuccess("");

    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  useEffect(() => {
    const requestedId = searchParams.get("edit");
    if (!requestedId || !products.length || autoOpenedRef.current === requestedId) return;
    const product = products.find((item) => String(item.id || item._id) === requestedId);
    if (product) {
      autoOpenedRef.current = requestedId;
      handleEdit(product);
    }
  }, [products, searchParams]);

  useEffect(() => {
    const requestedSearch = searchParams.get("search");
    if (requestedSearch) setSearchText(requestedSearch);
  }, [searchParams]);

  const handleDelete = async (product) => {
    const id = product.id || product._id;

    if (!id) {
      setError("Product ID is missing.");
      return;
    }

    if (!window.confirm(`Delete ${product.title}? This will soft-delete the product.`)) {
      return;
    }

    setError("");
    setSuccess("");

    try {
      const response = await fetch(buildApiUrl(`/products/${id}`), {
        method: "DELETE",
        credentials: "include",
        headers: { Accept: "application/json" },
      });

      const data = await readJsonResponse(response, "Delete Product API");

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Could not delete product.");
      }

      setSuccess("Product deleted successfully.");

      if (editingId === id) {
        setEditingId(null);
        setEditingTitle("");
        setForm(emptyForm);
      }

      fetchProducts();
    } catch (err) {
      setError(err.message || "Could not delete product.");
    }
  };

  const handleStatusChange = async (product, status) => {
    const id = product.id || product._id;
    setUpdatingProductId(id);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(buildApiUrl(`/products/${id}/status`), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ status }),
      });
      const data = await readJsonResponse(response, "Product Status API");
      if (!response.ok || !data.success) throw new Error(data.message || "Could not update status.");

      setProducts((current) => current.map((item) =>
        String(item.id || item._id) === String(id) ? { ...item, status } : item
      ));
      setSuccess(status === "published" ? `${product.title} is now published.` : `${product.title} is now unpublished.`);
    } catch (err) {
      setError(err.message || "Could not update status.");
    } finally {
      setUpdatingProductId(null);
    }
  };

  const handleUseFirstImage = async (product, firstBase) => {
    const id = product.id || product._id;
    if (!firstBase) return;
    setUpdatingProductId(id);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(buildApiUrl(`/admin/image-optimizer/products/${id}/card-image`), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ cardBase: firstBase }),
      });
      const data = await readJsonResponse(response, "Cover Image API");
      if (!response.ok || !data.success) throw new Error(data.message || "Could not set the first image.");

      setOptimizationStatuses((current) => ({
        ...current,
        [id]: { ...current[id], cardBase: firstBase, databaseSynced: true },
      }));
      await fetchProducts();
      setSuccess(`The first image is now the cover for ${product.title}.`);
    } catch (err) {
      setError(err.message || "Could not set the first image.");
    } finally {
      setUpdatingProductId(null);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingTitle("");
    setForm(emptyForm);
    setError("");
    setSuccess("");
  };

  const clearFilters = () => {
    setSearchText("");
    setStatusFilter("all");
    setFeaturedFilter("all");
    setSortBy("product-id-asc");
  };

  const handleLogout = async () => {
    try {
      await fetch(buildApiUrl("/admin/auth/logout"), {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json" },
      });
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      navigate("/admin-login", { replace: true });
    }
  };

  return (
    <div className="admin-products-page">
      <aside className="admin-products-sidebar">
        <div className="admin-products-brand">
          <span className="admin-logo-script">MayaChitro</span>
          <span className="admin-label">Admin Panel</span>
        </div>

        <nav className="admin-products-menu">
          <Link to="/admin">Dashboard</Link>
          <Link className="active" to="/admin/products">
            Photos / Products
          </Link>
          <Link to="/admin/orders">Orders & Sales</Link>
          <Link to="/admin/newsletter">Newsletter</Link>
          <Link to="/admin/hero-slides">Hero Slides</Link>
          <Link to="/admin/customers">Customers</Link>
          <Link to="/admin/image-optimizer">Image Optimizer</Link>

          <button type="button" className="admin-products-logout" onClick={handleLogout}>
            Logout
          </button>
        </nav>
      </aside>

      <main className="admin-products-main">
        <header className="admin-products-header">
          <div>
            <p className="eyebrow">Marketplace Control</p>
            <h1>Photos / Products</h1>
            <p>Create and manage your original MayaChitro photography.</p>
          </div>

          <div className="admin-products-actions">
            <button type="button" onClick={fetchProducts}>
              {isLoading ? "Refreshing..." : "Refresh"}
            </button>
            <Link to="/shop">View Shop</Link>
          </div>
        </header>

        <section className="admin-products-stats">
          <div>
            <span>Total Products</span>
            <strong>{stats.total}</strong>
          </div>
          <div>
            <span>Published</span>
            <strong>{stats.published}</strong>
          </div>
          <div>
            <span>Unpublished</span>
            <strong>{stats.unpublished}</strong>
          </div>
          <div>
            <span>Featured</span>
            <strong>{stats.featured}</strong>
          </div>
        </section>

        <section className="admin-products-form-card" ref={formRef}>
          <div className="admin-products-card-head">
            <div>
              <h2>{editingId ? "Edit Product" : "Add New Product"}</h2>
              <p>
                {editingId
                  ? `Editing: ${editingTitle}`
                  : "Add product details for an original MayaChitro photograph."}
              </p>
            </div>

            {editingId && (
              <span className="editing-pill">Editing ID: {editingId}</span>
            )}
          </div>

          {error && <div className="admin-products-error">{error}</div>}
          {success && <div className="admin-products-success">{success}</div>}

          <form onSubmit={handleSubmit} className="admin-products-form">
            <label>
              Product ID *
              <input
                name="productId"
                value={form.productId}
                onChange={handleChange}
                placeholder="MC-0001"
              />
            </label>

            <label>
              Product Type *
              <select
                name="productType"
                value={form.productType}
                onChange={handleChange}
              >
                <option value="photo_download">Photo Download</option>
                <option value="photo_license">Photo License</option>
              </select>
            </label>

            <label>
              Status
              <select name="status" value={form.status} onChange={handleChange}>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="hidden">Hidden</option>
                <option value="archived">Archived</option>
              </select>
            </label>

            <label className="wide">
              Title *
              <input
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="Golden River Sunset"
              />
            </label>

            <label>
              Slug *
              <input
                name="slug"
                value={form.slug}
                onChange={handleChange}
                placeholder="golden-river-sunset"
              />
            </label>

            <label>
              Price *
              <input
                name="price"
                type="number"
                step="0.01"
                value={form.price}
                onChange={handleChange}
                placeholder="9.99"
              />
            </label>

            <label>
              Category
              <input
                name="category"
                value={form.category}
                onChange={handleChange}
                placeholder="Sunset / Wall Art"
              />
            </label>

            <label>
              Collection
              <input
                name="collection"
                value={form.collection}
                onChange={handleChange}
                placeholder="Bangladesh Sunset"
              />
            </label>

            <label className="wide">
              Image Path
              <input
                name="image"
                value={form.image}
                onChange={handleChange}
                placeholder="/uploads/public/products/..."
              />
            </label>

            <label>
              Badge
              <input
                name="badge"
                value={form.badge}
                onChange={handleChange}
                placeholder="BEST SELLER / NEW / PRINTABLE"
              />
            </label>

            <label className="checkbox-label">
              <input
                name="featured"
                type="checkbox"
                checked={form.featured}
                onChange={handleChange}
              />
              Featured Product
            </label>

            <label className="checkbox-label homepage-latest-toggle">
              <input
                name="homepageLatest"
                type="checkbox"
                checked={form.homepageLatest}
                onChange={handleChange}
              />
              Show in Latest Photo Downloads
            </label>

            <label>
              Homepage display order
              <input
                name="homepageLatestOrder"
                type="number"
                min="1"
                step="1"
                value={form.homepageLatestOrder}
                onChange={handleChange}
                disabled={!form.homepageLatest}
                placeholder="1 = first"
              />
            </label>

            <label className="wide">
              Short Description
              <textarea
                name="shortDescription"
                value={form.shortDescription}
                onChange={handleChange}
                rows="2"
              />
            </label>

            <label className="wide">
              Full Description
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows="4"
              />
            </label>

            <label className="wide">
              Story
              <textarea
                name="story"
                value={form.story}
                onChange={handleChange}
                rows="4"
              />
            </label>

            <label className="wide">
              License Options
              <textarea
                name="licenseOptionsText"
                value={form.licenseOptionsText}
                onChange={handleChange}
                rows="3"
                placeholder="Personal Digital Download:9.99"
              />
            </label>

            <label className="wide">
              Includes
              <textarea
                name="includesText"
                value={form.includesText}
                onChange={handleChange}
                rows="3"
              />
            </label>

            <label className="wide">
              Tags
              <textarea
                name="tagsText"
                value={form.tagsText}
                onChange={handleChange}
                rows="3"
                placeholder="sunset&#10;river&#10;bangladesh"
              />
            </label>

            <label className="wide">
              SEO Title
              <input name="seoTitle" value={form.seoTitle} onChange={handleChange} />
            </label>

            <label className="wide">
              Meta Description
              <textarea
                name="metaDescription"
                value={form.metaDescription}
                onChange={handleChange}
                rows="3"
              />
            </label>

            <label className="wide">
              Alt Text
              <input name="altText" value={form.altText} onChange={handleChange} />
            </label>

            <div className="admin-products-form-actions wide">
              <button type="submit" disabled={isSaving}>
                {isSaving
                  ? "Saving..."
                  : editingId
                  ? "Update Product"
                  : "Add Product"}
              </button>

              {editingId && (
                <button
                  type="button"
                  className="secondary"
                  onClick={cancelEdit}
                  disabled={isSaving}
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </section>

        <section className="admin-products-table-card" ref={listRef}>
          <div className="table-header-row">
            <div>
              <h2>Product List</h2>
              <p>
                Showing <span>{filteredProducts.length}</span> of{" "}
                <span>{products.filter((item) => item.status !== "deleted").length}</span>{" "}
                active products.
              </p>
            </div>
          </div>

          <div className="admin-products-filters">
            <input
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Search product ID, title, slug, category..."
            />

            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="hidden">Hidden</option>
              <option value="archived">Archived</option>
            </select>

            <select
              value={featuredFilter}
              onChange={(event) => setFeaturedFilter(event.target.value)}
            >
              <option value="all">Featured + Normal</option>
              <option value="featured">Featured Only</option>
              <option value="not-featured">Not Featured</option>
            </select>

            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
              <option value="product-id-asc">Product ID · MC-0001 to MC-9999</option>
              <option value="product-id-desc">Product ID · Highest to Lowest</option>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="price-high">Price High to Low</option>
              <option value="price-low">Price Low to High</option>
              <option value="title">Title A to Z</option>
            </select>

            <button type="button" onClick={clearFilters}>
              Clear
            </button>
          </div>

          <div className="admin-products-table-wrap">
            <table className="admin-products-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th><button type="button" className="sortable-table-heading" onClick={() => setSortBy((current) => current === "product-id-asc" ? "product-id-desc" : "product-id-asc")}>Product {sortBy === "product-id-asc" ? "↑" : sortBy === "product-id-desc" ? "↓" : "↕"}</button></th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Latest section</th>
                  <th>Optimization</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredProducts.length ? (
                  filteredProducts.map((product) => {
                    const image = getImageSrc(getProductImage(product));
                    const optimization = optimizationStatuses[product.id || product._id] || {};

                    return (
                      <tr key={product.id || product._id || product.slug}>
                        <td>
                          {image ? (
                            <img
                              src={image}
                              alt={product.title || "Product"}
                              className="admin-product-thumb"
                            />
                          ) : (
                            <div className="admin-product-thumb empty-thumb">No Image</div>
                          )}
                        </td>

                        <td>
                          <strong>{product.title}</strong>
                          <small>{product.productId || product.productCode}</small>
                          <small>{product.slug}</small>
                        </td>

                        <td>${Number(product.price || 0).toFixed(2)}</td>

                        <td>
                          <select
                            className={`table-status-select ${product.status}`}
                            value={product.status === "published" ? "published" : "draft"}
                            disabled={updatingProductId === (product.id || product._id)}
                            onChange={(event) => handleStatusChange(product, event.target.value)}
                            aria-label={`Status for ${product.title}`}
                          >
                            <option value="published">Published</option>
                            <option value="draft">Unpublished</option>
                          </select>
                        </td>

                        <td>
                          {product.homepageLatest || product.homepage_latest
                            ? `Yes · #${product.homepageLatestOrder ?? product.homepage_latest_order ?? 1}`
                            : "No"}
                        </td>

                        <td>
                          <span className={`optimization-pill ${optimization.optimized ? optimization.databaseSynced ? "optimized" : "sync-needed" : "not-optimized"}`}>
                            {optimization.optimized ? optimization.databaseSynced ? "✓ Optimized" : "! Needs DB sync" : "Not optimized"}
                          </span>
                          {optimization.outputCount ? <small>{optimization.outputCount} files</small> : null}
                        </td>

                        <td>
                          <div className="table-actions">
                            <Link className="optimize-product-link" to={`/admin/image-optimizer?product=${product.id || product._id}`}>
                              {optimization.optimized ? "Choose cover" : "Optimize"}
                            </Link>
                            {optimization.optimized && optimization.firstBase && (
                              <button
                                type="button"
                                className="secondary-action"
                                disabled={updatingProductId === (product.id || product._id) || optimization.cardBase === optimization.firstBase}
                                onClick={() => handleUseFirstImage(product, optimization.firstBase)}
                              >
                                {optimization.cardBase === optimization.firstBase ? "First selected" : "Use first image"}
                              </button>
                            )}
                            <button type="button" onClick={() => handleEdit(product)}>
                              Edit
                            </button>
                            <button
                              type="button"
                              className="danger"
                              onClick={() => handleDelete(product)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td className="empty-table" colSpan="7">
                      No products found with the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}

export default AdminProducts;
